import React from 'react';
import PropTypes from 'prop-types';
import { Box, Card, CardContent, Chip, Divider, Stack, Typography } from '@mui/material';

/**
 * The card layout every mobile list uses.
 *
 * Each of these lists had grown its own copy of the same thing: a hard-bordered box holding a flat
 * run of reactstrap Labels, each one a "Key: value" template string. Nothing was emphasised over
 * anything else, so finding a unit number meant reading every line, and the values were marked up as
 * form labels for inputs that do not exist.
 *
 * This gives the lists one shape: what the row is about at the top, its state beside it, the detail
 * underneath as label-and-value pairs, and any actions at the bottom where they cannot be mistaken
 * for content.
 */
export default function MobileCard(props) {
    const { title, subtitle, status, fields, actions } = props;

    const visibleFields = (fields ?? []).filter((field) => field && field.value !== undefined);

    return (
        <Card
            elevation={0}
            sx={{
                marginBottom: '12px',
                border: '1px solid rgba(0, 0, 0, 0.1)',
                borderRadius: 2,
            }}
        >
            <CardContent sx={{ padding: '16px', '&:last-child': { paddingBottom: '16px' } }}>
                <Stack
                    direction={'row'}
                    spacing={1}
                    alignItems={'flex-start'}
                    justifyContent={'space-between'}
                >
                    <Box sx={{ minWidth: 0 }}>
                        <Typography
                            variant={'h6'}
                            component={'h3'}
                            sx={{ fontWeight: 700, lineHeight: 1.25, wordBreak: 'break-word' }}
                        >
                            {title}
                        </Typography>
                        {subtitle &&
                            <Typography
                                variant={'body2'}
                                sx={{ color: 'text.secondary', marginTop: '2px', wordBreak: 'break-word' }}
                            >
                                {subtitle}
                            </Typography>
                        }
                    </Box>

                    {status &&
                        <Chip
                            size={'small'}
                            label={status.label}
                            color={status.color ?? 'default'}
                            sx={{ flexShrink: 0 }}
                        />
                    }
                </Stack>

                {visibleFields.length > 0 &&
                    <>
                        <Divider sx={{ marginY: '12px' }} />

                        {/* Label and value as a two-column grid, so the values line up and can be
                            scanned down rather than read sentence by sentence. */}
                        <Box
                            sx={{
                                display: 'grid',
                                gridTemplateColumns: 'minmax(96px, auto) 1fr',
                                columnGap: '12px',
                                rowGap: '6px',
                                alignItems: 'baseline',
                            }}
                        >
                            {visibleFields.map((field) => (
                                <React.Fragment key={field.label}>
                                    <Typography
                                        variant={'caption'}
                                        sx={{
                                            color: 'text.secondary',
                                            textTransform: 'uppercase',
                                            letterSpacing: '0.04em',
                                            fontWeight: 600,
                                        }}
                                    >
                                        {field.label}
                                    </Typography>
                                    <Typography variant={'body2'} sx={{ wordBreak: 'break-word' }}>
                                        {field.value === null || field.value === '' ? '—' : field.value}
                                    </Typography>
                                </React.Fragment>
                            ))}
                        </Box>
                    </>
                }

                {actions &&
                    <Stack
                        direction={'row'}
                        spacing={1}
                        useFlexGap
                        sx={{ flexWrap: 'wrap', marginTop: '16px' }}
                    >
                        {actions}
                    </Stack>
                }
            </CardContent>
        </Card>
    );
}

MobileCard.propTypes = {
    /** What the row is, shown most prominently: a unit name, a transaction id, a tenant. */
    title: PropTypes.node.isRequired,

    /** Secondary identification under the title. */
    subtitle: PropTypes.node,

    /** A short state, shown as a chip beside the title. */
    status: PropTypes.shape({
        label: PropTypes.node.isRequired,
        color: PropTypes.string,
    }),

    /** The detail rows. A field with an undefined value is omitted; null or '' renders as a dash. */
    fields: PropTypes.arrayOf(PropTypes.shape({
        label: PropTypes.string.isRequired,
        value: PropTypes.node,
    })),

    actions: PropTypes.node,
};
